import { useState, type ReactNode } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, BotonAccion, formatFecha, confirmar } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';

interface FilaConvocatoria {
  id: string;
  estado: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  salon_nombre: string;
  cliente_nombre: string;
  categoria_nombre: string;
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function tituloMes(clave: string): string {
  const [y, m] = clave.split('-');
  return `${MESES[parseInt(m, 10) - 1]} ${y}`;
}

export function HistorialPersonalPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<FilaConvocatoria[]>('/api/convocatorias');
  const [mesAbierto, setMesAbierto] = useState<string | null>(null);

  async function vaciarHistorial() {
    // Doble confirmación (mismo criterio que la app).
    if (!confirmar('¿Borrar el historial? Se quitan de tu vista las convocatorias viejas (aceptadas, rechazadas y vencidas).')) return;
    if (!confirmar('¿Estás seguro? Esto solo limpia TU historial. La empresa conserva los registros de asistencia y pagos.')) return;
    try {
      await apiPost('/api/convocatorias/ocultar-historial');
      recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  }

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;

  const hoyStr = new Date().toISOString().slice(0, 10);
  const pasadas = (datos ?? []).filter((c) => c.fecha < hoyStr);
  const porMes = new Map<string, FilaConvocatoria[]>();
  for (const c of [...pasadas].sort((a, b) => b.fecha.localeCompare(a.fecha))) {
    const k = c.fecha.slice(0, 7);
    if (!porMes.has(k)) porMes.set(k, []);
    porMes.get(k)!.push(c);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-neutro-800">Historial</h2>
        {pasadas.length > 0 && (
          <BotonAccion variante="peligro" onClick={() => void vaciarHistorial()}>🗑 Vaciar historial</BotonAccion>
        )}
      </div>

      {pasadas.length === 0 ? (
        <Vacio texto="Todavía no hay servicios pasados." />
      ) : (
        Array.from(porMes.entries()).map(([mes, items]) => {
          const trabajados = items.filter((i) => i.estado === 'aceptada').length;
          return (
            <section key={mes}>
              <button
                className="flex w-full items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm border border-contorno-var text-left"
                onClick={() => setMesAbierto(mesAbierto === mes ? null : mes)}
              >
                <div>
                  <h3 className="text-base font-bold text-neutro-800">{tituloMes(mes)}</h3>
                  <p className="text-xs text-neutro-400">
                    {items.length} servicio{items.length > 1 ? 's' : ''} · {trabajados} trabajado{trabajados !== 1 ? 's' : ''}
                  </p>
                </div>
                <span className="text-lg font-bold text-primario-400">{mesAbierto === mes ? '▾' : '▸'}</span>
              </button>
              {mesAbierto === mes && (
                <ul className="mt-2 space-y-2">
                  {items.map((c) => {
                    const color = c.estado === 'aceptada' ? 'text-estado-ok'
                      : (c.estado === 'rechazada' || c.estado === 'vencida') ? 'text-estado-error' : 'text-neutro-500';
                    return (
                      <li key={c.id} className="rounded-xl bg-white p-3 text-sm shadow-sm border-l-4 border border-contorno-var border-l-primario-400/40">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-neutro-800">{formatFecha(c.fecha)}</span>
                          <span className={`text-xs font-semibold capitalize ${color}`}>{c.estado}</span>
                        </div>
                        <p className="text-neutro-500">{formatoRango(c.hora_inicio, c.hora_fin)} · {c.cliente_nombre} · {c.salon_nombre}</p>
                        <p className="font-medium text-primario-400">{c.categoria_nombre}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
